import fondoFooter from '@/assets/images/footer/fondo-footer.svg'
import logoFooterBottom from '@/assets/images/footer/logo-footer-bottom.svg'
import logoFooter from '@/assets/images/footer/logo-footer.svg'
import { useFormik } from 'formik'
import { motion } from 'framer-motion'
import { Linkedin } from 'lucide-react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import * as Yup from 'yup'
import { Typography } from '../../atoms/Typography/Typography'

interface NewsletterFormValues {
  email: string
}

const FooterSynapses: React.FC = () => {
  const { t } = useTranslation()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const newsletterValidationSchema = Yup.object({
    email: Yup.string()
      .email(t('footer.newsletter.validation.emailInvalid'))
      .required(t('footer.newsletter.validation.emailRequired'))
  })

  const newsletterFormik = useFormik<NewsletterFormValues>({
    initialValues: {
      email: ''
    },
    validationSchema: newsletterValidationSchema,
    onSubmit: async (_values, { resetForm }) => {
      setIsSubmitting(true)
      try {
        // Simular llamada API
        await new Promise(resolve => setTimeout(resolve, 1000))
        toast.success(t('footer.newsletter.successMessage'))
        resetForm()
      } catch (error) {
        toast.error(t('footer.newsletter.errorMessage'))
      } finally {
        setIsSubmitting(false)
      }
    }
  })

  return (
    <footer className='relative overflow-hidden text-white pt-20 pb-10 lg:pl-[300px] lg:pr-[100px]'>
      {/* Fondo con banner y overlay */}
      <div className='absolute inset-0 z-0'>
        <img
          src={fondoFooter}
          alt={t('footer.backgroundAlt')}
          className='w-full h-full object-cover'
        />
        <div className='absolute inset-0 bg-[#2D313B]/90' />
      </div>

      {/* Logo en el bottom absoluto */}
      <div className='absolute bottom-0 left-0 right-0 flex justify-center z-0'>
        <img
          src={logoFooterBottom}
          alt={t('footer.logoAlt')}
          className='w-full object-contain opacity-50'
        />
      </div>

      {/* Contenido principal */}
      <motion.div
        className='container mx-auto relative z-10'
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.5 }}
      >
        <div className='grid grid-cols-1 md:grid-cols-4 gap-12 mb-16'>
          {/* Columna 1 - Logo y newsletter */}
          <div className='md:col-span-2'>
            <img src={logoFooter} alt={t('footer.logoAlt')} />
            <Typography variant='p' className='mb-6 text-white mt-8'>
              {t('footer.newsletter.description')}
            </Typography>

            <form onSubmit={newsletterFormik.handleSubmit} className='mb-2'>
              <div className='flex gap-2'>
                <div className='flex-1'>
                  <input
                    type='email'
                    name='email'
                    placeholder={t('footer.newsletter.placeholder')}
                    value={newsletterFormik.values.email}
                    onChange={newsletterFormik.handleChange}
                    onBlur={newsletterFormik.handleBlur}
                    className={`w-full bg-white/10 border ${
                      newsletterFormik.touched.email !== undefined && newsletterFormik.errors.email !== undefined
                        ? 'border-red-500'
                        : 'border-white/20'
                    } rounded px-4 py-3 text-white placeholder-white/50`}
                  />
                  {newsletterFormik.touched.email !== undefined && newsletterFormik.errors.email !== undefined && (
                    <div className='text-red-500 text-sm mt-1'>{newsletterFormik.errors.email}</div>
                  )}
                </div>
                <button
                  type='submit'
                  disabled={isSubmitting}
                  className='bg-[#4DEEDB] text-[#18181B] font-medium px-6 py-3 rounded hover:bg-[#3ad8c5] transition-colors disabled:opacity-50'
                >
                  {isSubmitting ? t('footer.newsletter.submitting') : t('footer.newsletter.subscribe')}
                </button>
              </div>
            </form>

            <Typography variant='p' className='text-white text-sm'>
              {t('footer.newsletter.additionalText')}
            </Typography>
          </div>

          {/* Columna 2 - Contact */}
          <div />

          {/* Columna 2 - Company */}
          <div className='grid grid-cols-2 gap-6'>
            <div className='w-auto'>
              <Typography variant='h6' className='mb-6 text-white font-semibold'>
                {t('footer.links.company.title')}
              </Typography>
              <ul className='space-y-3 text-white'>
                <li><a href='#' className='hover:text-white transition-colors'>{t('footer.links.company.about')}</a></li>
                <li><a href='#' className='hover:text-white transition-colors'>{t('footer.links.company.services')}</a></li>
              </ul>
            </div>

            {/* Columna 3 - Follow Us */}
            <div>
              <Typography variant='h6' className='mb-6 text-white font-semibold'>
                {t('footer.links.follow.title')}
              </Typography>
              <div className='grid grid-cols-1 gap-4'>
                {/* <a href='#' className='flex items-center gap-2 text-white hover:text-white transition-colors'>
                  <Facebook size={18} />
                  <span>{t('footer.links.follow.facebook')}</span>
                </a>
                <a href='#' className='flex items-center gap-2 text-white hover:text-white transition-colors'>
                  <Instagram size={18} />
                  <span>{t('footer.links.follow.instagram')}</span>
                </a>
                <a href='#' className='flex items-center gap-2 text-white hover:text-white transition-colors'>
                  <Twitter size={18} />
                  <span>{t('footer.links.follow.twitter')}</span>
                </a> */}
                <a href='#' className='flex items-center gap-2 text-white hover:text-white transition-colors'>
                  <Linkedin size={18} />
                  <span>{t('footer.links.follow.linkedin')}</span>
                </a>
                {/* <a href='#' className='flex items-center gap-2 text-white hover:text-white transition-colors'>
                  <Youtube size={18} />
                  <span>{t('footer.links.follow.youtube')}</span>
                </a> */}
              </div>
            </div>
          </div>
        </div>

        {/* Copyright y enlaces legales */}
        <div className='border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center'>
          <Typography variant='p' className='text-white text-sm mb-4 md:mb-0'>
            {t('footer.copyright', { year: new Date().getFullYear() })}
          </Typography>
          <div className='flex gap-6'>
            <a href='#' className='text-white text-sm hover:underline'>{t('footer.legal.terms')}</a>
            <a href='#' className='text-white text-sm hover:underline'>{t('footer.legal.privacy')}</a>
            <a href='#' className='text-white text-sm hover:underline'>{t('footer.legal.cookies')}</a>
          </div>
        </div>
      </motion.div>
    </footer>
  )
}

export default FooterSynapses
